/**
 * History Slider Component
 * Handles vertical sliding timeline functionality with navigation and autoplay
 */
import { debugLog } from '../debug/logger.js'

class HistorySlider {
	constructor() {
		// Configuration
		this.CONFIG = {
			SELECTORS: {
				slider: '.ui-slider-history',
				container: '.ui-swiper-container',
				slide: '.ui-swiper-slide',
				dot: '.ui-pagination-dot',
				navItem: '.ui-nav-item',
				prevBtn: '.ui-arrow-prev',
				nextBtn: '.ui-arrow-next',
			},
			TIMING: {
				TRANSITION: 500,
				AUTOPLAY: 5000,
			},
			STATES: {
				navItem: {
					active: ['opacity-100', 'text-3xl'],
					inactive: ['opacity-50'],
				},
				dot: {
					active: ['bg-gray-900'],
					inactive: ['bg-gray-200'],
				},
			},
		}

		// Initialize
		this.initAll()
	}

	initAll() {
		const sliders = document.querySelectorAll(this.CONFIG.SELECTORS.slider)
		sliders.forEach(slider => this.initSlider(slider))

		if (sliders.length > 0) {
			debugLog.success('HistorySlider: Initialized', sliders.length, 'sliders')
		}
	}

	initSlider(sliderElement) {
		const elements = {
			slider: sliderElement,
			container: sliderElement.querySelector(this.CONFIG.SELECTORS.container),
			slides: sliderElement.querySelectorAll(this.CONFIG.SELECTORS.slide),
			dots: sliderElement.querySelectorAll(this.CONFIG.SELECTORS.dot),
			navItems: sliderElement.querySelectorAll(this.CONFIG.SELECTORS.navItem),
			prevBtn: sliderElement.querySelector(this.CONFIG.SELECTORS.prevBtn),
			nextBtn: sliderElement.querySelector(this.CONFIG.SELECTORS.nextBtn),
		}

		if (!elements.container || elements.slides.length === 0) {
			debugLog.warn('HistorySlider: No slides found')
			return
		}

		// Adjust text boxes for slides without images
		this.adjustTextBoxes(elements)

		// State
		const state = {
			currentSlide: 0,
			isTransitioning: false,
			autoplayInterval: null,
		}

		// Store on element
		sliderElement.historyState = state

		// Bind methods
		const updateSlider = (index, smooth = true) => {
			this.updateSlider(elements, state, index, smooth)
		}
		const startAutoplay = () => {
			this.startAutoplay(elements, state, updateSlider)
		}
		const resetAutoplay = () => {
			this.resetAutoplay(state, startAutoplay)
		}

		// Bind events
		this.bindEvents(elements, state, updateSlider, resetAutoplay)

		// Initialize
		updateSlider(0, false)
		startAutoplay()

		debugLog.info('HistorySlider: Initialized slider')
	}

	adjustTextBoxes(elements) {
		elements.slides.forEach(slide => {
			const noImage = slide.querySelector('.ui-no-image')
			const textBox = slide.querySelector('.ui-text-box')

			if (noImage && textBox) {
				// If there's no image, make the text box full height and width
				textBox.style.height = '100%'
				textBox.style.width = '100%'
				textBox.style.bottom = 'auto'
				textBox.style.top = '0'
				textBox.style.left = '0'
				textBox.classList.add('flex', 'items-center')
				debugLog.log('HistorySlider: Adjusted text box for slide without image')
			}
		})
	}

	updateSlider(elements, state, index, smooth = true) {
		if (state.isTransitioning) return
		if (index < 0 || index >= elements.slides.length) return

		state.isTransitioning = true
		state.currentSlide = index

		// Handle smooth transitions
		if (smooth) {
			elements.container.classList.add('transition-transform', 'duration-500', 'ease-in-out')
		} else {
			elements.container.classList.remove('transition-transform', 'duration-500', 'ease-in-out')
		}

		// Update slider position (vertical sliding)
		elements.container.style.transform = `translateY(-${index * 100}%)`

		// Update navigation items
		this.updateNavItems(elements, state, index)

		// Update pagination dots
		this.updateDots(elements, state, index)

		// Reset transition flag
		setTimeout(() => {
			state.isTransitioning = false
		}, this.CONFIG.TIMING.TRANSITION)

		debugLog.log('HistorySlider: Moved to slide', index)
	}

	updateNavItems(elements, state, index) {
		const { STATES } = this.CONFIG
		const MAX_VISIBLE = 11 // Always show 11 items
		const totalItems = elements.navItems.length

		// If there are 11 or fewer items, show all
		if (totalItems <= MAX_VISIBLE) {
			elements.navItems.forEach((item, i) => {
				item.classList.remove(...STATES.navItem.active, ...STATES.navItem.inactive, 'text-xl')
				item.style.display = ''

				if (i === index) {
					item.classList.add(...STATES.navItem.active)
				} else {
					item.classList.add(...STATES.navItem.inactive)
				}
			})
			return
		}

		// Calculate the window: always show 11 items centered around active
		const halfWindow = Math.floor(MAX_VISIBLE / 2) // 5
		let startIndex = index - halfWindow
		let endIndex = index + halfWindow

		// Adjust window if at the beginning
		if (startIndex < 0) {
			endIndex += Math.abs(startIndex)
			startIndex = 0
		}

		// Adjust window if at the end
		if (endIndex >= totalItems) {
			const overflow = endIndex - totalItems + 1
			startIndex = Math.max(0, startIndex - overflow)
			endIndex = totalItems - 1
		}

		elements.navItems.forEach((item, i) => {
			item.classList.remove(...STATES.navItem.active, ...STATES.navItem.inactive, 'text-xl')

			// Show items within the window
			if (i >= startIndex && i <= endIndex) {
				item.style.display = ''

				if (i === index) {
					item.classList.add(...STATES.navItem.active)
				} else {
					item.classList.add(...STATES.navItem.inactive)
				}
			} else {
				item.style.display = 'none'
			}
		})
	}

	updateDots(elements, state, index) {
		const { STATES } = this.CONFIG

		elements.dots.forEach((dot, i) => {
			if (i === index) {
				this.swapClasses(dot, STATES.dot.inactive, STATES.dot.active)
			} else {
				this.swapClasses(dot, STATES.dot.active, STATES.dot.inactive)
			}
		})
	}

	startAutoplay(elements, state, updateSlider) {
		if (elements.slides.length <= 1) return
		if (state.autoplayInterval) return

		state.autoplayInterval = setInterval(() => {
			const newIndex = state.currentSlide < elements.slides.length - 1 ? state.currentSlide + 1 : 0
			updateSlider(newIndex)
		}, this.CONFIG.TIMING.AUTOPLAY)
	}

	resetAutoplay(state, startAutoplay) {
		clearInterval(state.autoplayInterval)
		state.autoplayInterval = null
		startAutoplay()
	}

	bindEvents(elements, state, updateSlider, resetAutoplay) {
		// Previous button
		if (elements.prevBtn) {
			elements.prevBtn.addEventListener('click', () => {
				const newIndex = state.currentSlide > 0 ? state.currentSlide - 1 : elements.slides.length - 1
				updateSlider(newIndex)
				resetAutoplay()
			})
		}

		// Next button
		if (elements.nextBtn) {
			elements.nextBtn.addEventListener('click', () => {
				const newIndex = state.currentSlide < elements.slides.length - 1 ? state.currentSlide + 1 : 0
				updateSlider(newIndex)
				resetAutoplay()
			})
		}

		// Pagination dots
		elements.dots.forEach((dot, index) => {
			dot.addEventListener('click', () => {
				updateSlider(index)
				resetAutoplay()
			})
		})

		// Navigation items (sidebar)
		elements.navItems.forEach((item, index) => {
			item.addEventListener('click', () => {
				updateSlider(index)
				resetAutoplay()
			})
		})

		// Pause autoplay on hover
		elements.slider.addEventListener('mouseenter', () => {
			clearInterval(state.autoplayInterval)
			state.autoplayInterval = null
		})

		elements.slider.addEventListener('mouseleave', () => {
			this.startAutoplay(elements, state, updateSlider)
		})
	}

	swapClasses(element, remove, add) {
		if (!element) return
		element.classList.remove(...remove)
		element.classList.add(...add)
	}
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', () => new HistorySlider())
} else {
	new HistorySlider()
}

// Export for use
window.HistorySlider = HistorySlider
